import { convexToJson, jsonToConvex, version, STATUS_CODE_UDF_FAILED, } from "@convex-dev/common";
const inBrowser = typeof window !== "undefined" && typeof document !== "undefined";
const fetch = inBrowser
    ? window.fetch
    : (...args) => import("node-fetch").then(({ default: fetch }) => fetch(...args));
/**
 * A Convex client that runs queries and mutations over HTTP.
 *
 * This is appropriate for server-side code (like Netlify Lambdas) or non-reactive
 * webapps.
 *
 * If you're building a React app, consider using
 * `@convex-dev/react:ReactClient` instead.
 *
 * @public
 */
export class HTTPConvexClient {
    constructor(address) {
        this.address = `${address}/api/${version}`;
    }
    /**
     * Obtain the {@link HTTPConvexClient}'s URL to its backend.
     *
     * @returns The URL to the Convex backend, including the client's API version.
     */
    backendUrl() {
        return this.address;
    }
    /**
     * Set the authentication token to be used for subsequent queries and mutations.
     * Should be called whenever the token changes (i.e. due to expiration and refresh)
     * @param value - JWT-encoded OpenID Connect Identity Token
     */
    setAuth(value) {
        this.auth = value;
    }
    /**
     * Clear the current authentication token if set.
     */
    clearAuth() {
        this.auth = undefined;
    }
    /**
     * Construct a new handle to a `Query`.
     *
     * @param name - The name of the query function.
     * @returns The `Query` object with that name.
     */
    query(name) {
        return {
            call: async (...args) => {
                // Interpret the arguments as a Convex array, and then serialize
                // it to JSON.
                const argsJSON = JSON.stringify(convexToJson(args));
                const argsComponent = encodeURIComponent(argsJSON);
                let url = `${this.address}/udf?path=${name}&args=${argsComponent}`;
                const headers = this.auth
                    ? { Authorization: `Bearer ${this.auth}` }
                    : {};
                const response = await fetch(url, {
                    credentials: "include",
                    headers: headers,
                });
                if (!response.ok && response.status != STATUS_CODE_UDF_FAILED) {
                    throw new Error(await response.text());
                }
                let respJSON = await response.json();
                let value = jsonToConvex(respJSON.value);
                for (const line of respJSON.logs) {
                    console.log(`%c[CONVEX Q(${name})] ${line}`, "color:blue");
                }
                if (!respJSON.success) {
                    throw new Error(`[CONVEX Q(${name})] ${value}`);
                }
                return value;
            },
        };
    }
    /**
     * Construct a new handle to a `Mutation`.
     *
     * @param name - The name of the mutation function.
     * @returns The `Mutation` object with that name.
     */
    mutation(name) {
        return {
            call: async (...args) => {
                // Interpret the arguments as a Convex array and then serialize to JSON.
                const body = JSON.stringify({
                    path: name,
                    args: convexToJson(args),
                    tokens: [],
                });
                let headers = {
                    "Content-Type": "application/json",
                };
                if (this.auth) {
                    headers["Authorization"] = `Bearer ${this.auth}`;
                }
                const response = await fetch(`${this.address}/udf`, {
                    body,
                    method: "POST",
                    headers: headers,
                    credentials: "include",
                });
                if (!response.ok && response.status != STATUS_CODE_UDF_FAILED) {
                    throw new Error(await response.text());
                }
                let respJSON = await response.json();
                const value = jsonToConvex(respJSON.value);
                for (const line of respJSON.logs) {
                    console.log(`%c[CONVEX T(${name})] ${line}`, "color:blue");
                }
                if (!respJSON.success) {
                    throw new Error(`[CONVEX T(${name})] ${value}`);
                }
                return value;
            },
        };
    }
}
//# sourceMappingURL=http_client.js.map